import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Flame, Printer } from "lucide-react";

const PRODUCTION_URL = "https://smokey-af-bbq.emergent.host";

export default function Poster() {
  return (
    <div className="min-h-screen bg-black text-white poster-root">
      {/* Print button — hidden on print */}
      <div className="no-print fixed top-4 left-1/2 -translate-x-1/2 z-50 flex gap-2">
        <button
          onClick={() => window.print()}
          data-testid="poster-print-btn"
          className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-white hover:text-black text-white font-['Anton'] tracking-widest uppercase px-5 py-3 text-xs transition-colors shadow-2xl"
        >
          <Printer className="w-4 h-4" />
          Print Poster
        </button>
        <a
          href="/"
          className="inline-flex items-center bg-black/60 backdrop-blur border border-white/20 hover:border-white text-white font-['Anton'] tracking-widest uppercase px-5 py-3 text-xs transition-colors"
        >
          Back to Site
        </a>
      </div>

      {/* Poster sheet (8.5x11 letter ratio at print) */}
      <div className="flex justify-center py-10 px-4">
        <div
          data-testid="poster-sheet"
          className="poster-sheet relative w-full max-w-[850px] aspect-[8.5/11] bg-black overflow-hidden border border-white/10"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 80% 60% at 50% 20%, rgba(255,59,48,0.18), transparent 60%), radial-gradient(ellipse 60% 40% at 50% 90%, rgba(255,140,0,0.15), transparent 60%)",
          }}
        >
          {/* Grain / smoke overlay */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none opacity-30"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.08 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
              mixBlendMode: "screen",
            }}
          />

          {/* Corner ornaments */}
          <div className="absolute top-6 left-6 right-6 flex items-center justify-between text-[10px] uppercase tracking-[0.4em] font-['Outfit'] text-zinc-500">
            <span>Est. 2026</span>
            <span className="text-[#FF8C00]">Low · Slow · Smokey</span>
          </div>

          {/* Top section */}
          <div className="relative h-full flex flex-col items-center justify-between py-20 px-10 text-center">
            <div>
              <Flame className="w-10 h-10 text-[#FF3B30] mx-auto" />
              <h1 className="font-['Anton'] text-7xl md:text-8xl tracking-tighter uppercase leading-[0.85] mt-6">
                Smokey
                <br />
                <span className="text-[#FF3B30]">AF</span> Barbecue
              </h1>
              <p className="mt-5 font-['Outfit'] italic text-[#FF8C00] text-lg">
                Hand-pulled. Wood-fired. Honest as hell.
              </p>
            </div>

            {/* QR */}
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-[#FF3B30]" />
                <span className="font-['Anton'] text-2xl tracking-[0.4em] uppercase text-white">
                  Scan to Order
                </span>
                <span className="h-px w-12 bg-[#FF3B30]" />
              </div>

              <div
                data-testid="poster-qr"
                className="mt-6 p-5 bg-white shadow-[0_25px_60px_-15px_rgba(255,59,48,0.4)]"
              >
                <QRCodeSVG
                  value={PRODUCTION_URL}
                  size={260}
                  level="H"
                  bgColor="#ffffff"
                  fgColor="#000000"
                  includeMargin={false}
                />
              </div>

              <p className="mt-5 font-['Outfit'] text-sm tracking-widest uppercase text-zinc-300">
                Point your camera. Tap the link. Eat.
              </p>
              <p className="mt-2 font-['Anton'] text-base tracking-widest uppercase text-zinc-500 break-all">
                {PRODUCTION_URL.replace(/^https?:\/\//, "")}
              </p>
            </div>

            {/* Menu preview */}
            <div className="w-full">
              <div className="grid grid-cols-3 gap-3 text-left font-['Outfit'] text-[11px] text-zinc-300">
                {[
                  { name: "BBQ Pizza", price: "$16" },
                  { name: "BBQ Boat", price: "$14" },
                  { name: "Bavarian Behemoth Pretzel", price: "$12" },
                  { name: "Pulled Pork Sandwich", price: "$14" },
                  { name: "Japanese BBQ Burger", price: "$14" },
                  { name: "Loaded Pork Fries", price: "$14" },
                  { name: "Pork Crack Mash Bowl", price: "$10" },
                  { name: "Mini Pulled Pork Sandwich", price: "$7" },
                  { name: "Mini BBQ Boat", price: "$7" },
                  { name: "Nacho Pork Sandwich", price: "$10" },
                  { name: "Japanese Pork Sandwich", price: "$10" },
                  { name: "Nacho Pork Pizza", price: "$16" },
                  { name: "BBQ Sushi", price: "$8" },
                  { name: "Pepperoni Pizza", price: "$16" },
                  { name: "Chicken Alfredo Pizza", price: "$16" },
                  { name: "Garlic Parm Wings (6)", price: "$12" },
                  { name: "BBQ Wings (6)", price: "$12" },
                  { name: "Buffalo Wings (6)", price: "$12" },
                  { name: "Chipotle Firecracker Wings (6)", price: "$12" },
                  { name: "Chicken Bacon Ranch Pizza", price: "$16" },
                  { name: "BBQ Chicken Sandwich", price: "$10" },
                  { name: "Order of Fries", price: "$5" },
                ].map((item) => (
                  <div
                    key={item.name}
                    className="border border-white/10 px-3 py-2 flex items-center justify-between"
                  >
                    <span className="uppercase tracking-wider">{item.name}</span>
                    <span className="font-['Anton'] text-[#FF8C00]">
                      {item.price}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-between text-[10px] uppercase tracking-[0.4em] font-['Outfit'] text-zinc-500">
                <span>Pay Card · Pay Cash</span>
                <span className="text-[#FF8C00]">@smokeyaf.bbq</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          @page {
            size: letter;
            margin: 0;
          }
          html, body {
            background: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print { display: none !important; }
          .poster-root { background: #000 !important; min-height: 0 !important; }
          .poster-root > div { padding: 0 !important; }
          .poster-sheet {
            width: 100% !important;
            max-width: none !important;
            height: 100vh !important;
            aspect-ratio: auto !important;
            border: none !important;
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}
